import React, { useEffect, useRef, useState } from 'react'

import { ModalBox } from '@fourthwall/components'

// Modal component following the established pattern
type IframeModalProps = {
  title: string
  src: string
  hmac: { signature: string; timestamp: string }
  shopId: string
  close: () => void
  onRefresh?: (refreshType: string) => void
}

export const IframeModal = ({ title, src, hmac, shopId, close, onRefresh }: IframeModalProps) => {
  const [iframeHeight, setIframeHeight] = useState(400)

  const iframeRef = useRef<HTMLIFrameElement>(null)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify the origin for security
      if (event.source !== iframeRef.current?.contentWindow) {
        return
      }

      const allowedOrigin = new URL(src).origin

      if (event.origin !== allowedOrigin) {
        console.error(`Received message from unauthorized origin: ${event.origin}`)

        return
      }

      try {
        const { type, data } = event.data

        switch (type) {
          case 'CLIPBOARD':
            navigator.clipboard.writeText(data.text)
            break

          case 'SET_HEIGHT':
            // Handle resize
            setIframeHeight(data.height)
            break

          case 'MODAL_CLOSE':
            close()

            // Pass refresh information back to parent
            if (data.refresh && onRefresh) {
              onRefresh(data.refresh)
            }
            break

          default:
            console.error(`Received unknown message type: ${type}`, data)
        }
      } catch (error) {
        console.error('Error processing postMessage:', error)
      }
    }

    // Add event listener
    window.addEventListener('message', handleMessage)

    // Cleanup function
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [close, onRefresh])

  return (
    <ModalBox
      title={<div style={{ padding: 16 }}>{title}</div>}
      size="large"
      onClose={close}
      withoutPadding
    >
      <iframe
        src={`${src}?hmac=${encodeURIComponent(hmac.signature)}&timestamp=${encodeURIComponent(hmac.timestamp)}&shop_id=${encodeURIComponent(shopId)}`}
        ref={iframeRef}
        width="100%"
        height={iframeHeight}
        style={{ border: 'none' }}
      />
    </ModalBox>
  )
}
